/**
 * React-query hooks for the `/v1/users` endpoint (B15-BE).
 *
 * Used by the adjudication flow to map annotator uuids to display
 * names. Names are stable, so `staleTime` is 5min — sharply higher than
 * the 30s used for "live count" reads in `useAnnotation.ts`.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { userService } from '../services/userService';
import type { UserSummary } from '../types';

/**
 * Stable query-key factory. Namespaced under `['golab', 'users']` to sit
 * alongside `['golab', 'annotation']` and friends.
 */
export const userKeys = {
  all: ['golab', 'users'] as const,
  byIds: (ids: string[]) => [...userKeys.all, 'byIds', ids] as const,
};

/**
 * Batch-fetch user summaries by id and return them as a
 * `userId → UserSummary` map for O(1) lookup at the call site.
 *
 * Key normalisation: ids are sorted before being placed in the cache
 * key so `["u1","u2"]` and `["u2","u1"]` hit the same cache entry.
 *
 * `enabled` short-circuits to `false` on empty input so we don't make a
 * pointless network call while a parent query is still loading.
 *
 * The transform from `UserSummary[]` → `Record<userId, UserSummary>`
 * runs in react-query's `select`, so the result is memoised; consumers
 * don't pay for an `Object.fromEntries` on every render.
 */
export const useUsersById = (ids: string[]) => {
  // Memoise the sorted list so consumers that pass an ad-hoc array
  // each render don't trigger a fresh query-key reference (and a
  // needless invalidation cascade) every time. The dep is `ids`
  // itself - callers that don't already memoise should, but this hook
  // degrades gracefully when they don't.
  const sortedIds = useMemo(() => [...ids].sort(), [ids]);
  return useQuery<UserSummary[], Error, Record<string, UserSummary>>({
    queryKey: userKeys.byIds(sortedIds),
    queryFn: () => userService.getUsersByIds(sortedIds),
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    select: (items) =>
      Object.fromEntries(items.map((u) => [u.userId, u])),
  });
};
